import React, { useMemo } from "react";
import { useTheme } from "@material-ui/core/styles";
import Skeleton from "@material-ui/lab/Skeleton";
import {
  BarChart,
  CartesianGrid,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import moment from "moment";
import Title from "./Title";

// Segunda série validada para contraste com o azul primário (claro / escuro).
const SECOND_SERIES = { light: "#eb6834", dark: "#d95926" };

/**
 * Período de 1 dia: conversas por hora.
 * Período maior: conversas abertas × finalizadas por dia.
 */
const Chart = ({ daily = [], heatmap = [], days = 1, loading }) => {
  const theme = useTheme();
  const isSingleDay = days <= 1;

  const data = useMemo(() => {
    if (isSingleDay) {
      const hours = Array.from({ length: 24 }, (_, i) => ({
        label: `${String(i).padStart(2, "0")}h`,
        started: 0,
      }));
      heatmap.forEach((cell) => {
        if (hours[cell.hour]) hours[cell.hour].started += cell.total;
      });
      return hours;
    }
    return daily.map((d) => ({
      label: moment(d.date).format("DD/MM"),
      started: d.started,
      finished: d.finished,
    }));
  }, [isSingleDay, daily, heatmap]);

  const total = data.reduce((sum, d) => sum + d.started, 0);
  const secondColor = SECOND_SERIES[theme.palette.type] || SECOND_SERIES.light;

  if (loading) return <Skeleton variant="rect" height="100%" style={{ borderRadius: 8 }} />;

  return (
    <React.Fragment>
      <Title>
        {isSingleDay
          ? `Conversas por hora: ${total}`
          : `Conversas por dia: ${total}`}
      </Title>
      <ResponsiveContainer>
        <BarChart
          data={data}
          margin={{ top: 16, right: 8, bottom: 0, left: -16 }}
          barGap={2}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={theme.palette.divider}
            vertical={false}
          />
          <XAxis
            dataKey="label"
            stroke={theme.palette.text.secondary}
            tick={{ fontSize: 11 }}
            interval="preserveStartEnd"
            minTickGap={8}
          />
          <YAxis
            type="number"
            allowDecimals={false}
            stroke={theme.palette.text.secondary}
            tick={{ fontSize: 11 }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 8,
              fontSize: 13,
              color: theme.palette.text.primary,
            }}
            cursor={{ fill: theme.palette.action.hover }}
          />
          {!isSingleDay && (
            <Legend
              verticalAlign="top"
              align="right"
              height={24}
              iconType="circle"
              wrapperStyle={{ fontSize: 12, color: theme.palette.text.secondary }}
            />
          )}
          <Bar
            dataKey="started"
            name="Conversas"
            fill={theme.palette.primary.main}
            radius={[4, 4, 0, 0]}
            maxBarSize={24}
          />
          {!isSingleDay && (
            <Bar
              dataKey="finished"
              name="Finalizados"
              fill={secondColor}
              radius={[4, 4, 0, 0]}
              maxBarSize={24}
            />
          )}
        </BarChart>
      </ResponsiveContainer>
    </React.Fragment>
  );
};

export default Chart;
